import type { LandingContent } from "./types";
import heroWallTies from "@/assets/remedial-wall-ties/walltie-hero.webp";
import wtBorescope from "@/assets/remedial-wall-ties/walltie-borescope.webp";
import wtBulging from "@/assets/remedial-wall-ties/walltie-card-bulging.webp";
import wtHorizontal from "@/assets/remedial-wall-ties/walltie-card-horizontal.webp";
import wtLintel from "@/assets/remedial-wall-ties/walltie-card-lintel.webp";
import wtStepped from "@/assets/remedial-wall-ties/walltie-card-stepped.webp";
import wtEvidence from "@/assets/remedial-wall-ties/walltie-evidence.webp";
import wtStep from "@/assets/remedial-wall-ties/walltie-step-1.webp";

/**
 * Body content for the /remedial-wall-ties page. Structural service,
 * lead with Structural Survey, tested/CE-marked tie systems, Fully
 * Insured and Insurance-Backed Guarantee. No MCS or PAS 2030 framing
 * on this page. Calm, non-alarmist tone throughout.
 *
 * Repositioning rules: no "independent" / "no commission" language;
 * no £ savings figures; no guarantee durations.
 */
export const remedialWallTiesLp: LandingContent = {
 pageKind: "wall-ties",
 slug: "remedial-wall-ties",
 path: "/lp/remedial-wall-ties",
 title: "Remedial Wall Tie Replacement In South Wales",
 metaTitle:
 "Remedial Wall Ties South Wales | Structural Survey | Tested Tie Systems",
 metaDescription:
 "Remedial wall tie replacement across South Wales. Borescope cavity inspection, structural assessment, tested CE-marked tie systems, fully insured, insurance-backed guarantee.",
 defaultService: "Remedial Wall Ties",
 heroHeadline: "Remedial Wall Tie Replacement In South Wales",
 heroSubheading:
 "Corroded or failed wall ties can affect a wall's structural integrity. Across Wales and the South of England, we inspect, confirm the cause, and replace with tested systems, only where it's actually needed.",
 heroBullets: [
 "Structural Survey",
 "Tested / CE-Marked Tie Systems",
 "Insurance-Backed Guarantee",
 "Fully Insured",
 "Free Inspection",
 ],
 heroImage: heroWallTies,
 heroAlt: "Brick wall showing horizontal cracking from corroded wall ties",
 heroFloatImages: [
  { src: heroWallTies, slug: "wt-hero" },
  { src: wtBorescope, slug: "wt-borescope" },
  { src: wtBulging, slug: "wt-bulging" },
  { src: wtHorizontal, slug: "wt-horizontal" },
  { src: wtLintel, slug: "wt-lintel" },
  { src: wtStepped, slug: "wt-stepped" },
  { src: wtEvidence, slug: "wt-evidence" },
  { src: wtStep, slug: "wt-step" },
 ],
 trustItems: [
 "Structural Survey",
 "Tested / CE-Marked Tie Systems",
 "Insurance-Backed Guarantee",
 "Fully Insured",
 "Free Inspection",
 ],
 problemTitle: "Why Wall Ties Matter",
 problemPoints: [
 {
 title: "Ties Hold The Two Skins Together",
 body: "In a cavity wall, the inner and outer leaves rely on embedded metal ties to act as one structural unit. If those ties corrode, that connection weakens over time.",
 },
 {
 title: "Corroding Ties Expand",
 body: "As older metal ties rust, they expand and push the surrounding mortar bed apart. This is what produces the characteristic horizontal cracking at regular intervals up a wall.",
 },
 {
 title: "It Isn't Always The Ties",
 body: "Cracking can also point to subsidence, thermal movement or sulphate attack. A proper inspection confirms the cause before any remedial work is recommended.",
 },
 ],
 clarityTitle: "What A Proper Wall Tie Inspection Looks Like",
 clarityBody:
 "A borescope inspection inside the cavity, a survey of crack patterns and locations across each elevation, and an assessment of tie type, density and condition against current engineering guidance, before we discuss what (if any) replacement is needed.",
 productTitle: "What We Replace With",
 productPoints: [
 {
 title: "Tested / CE-Marked Tie Systems",
 body: "Mechanical and resin-fixed remedial ties from tested systems, selected for your wall construction and exposure.",
 },
 {
 title: "Correct Density & Pattern",
 body: "Tie density and spacing specified to current engineering guidance, not arbitrary patterns.",
 },
 {
 title: "Corroded Ties Isolated Where Required",
 body: "Where existing ties continue to corrode, we isolate them so they no longer expand into the mortar bed.",
 },
 {
 title: "Neat Making-Good",
 body: "Drill holes and any opened mortar beds are made good to blend with the existing brickwork and pointing.",
 },
 ],
 processSteps: [
 {
 step: 1,
 title: "Structural Inspection",
 description:
 "Borescope cavity inspection, crack mapping across elevations and identification of existing tie type and condition.",
 icon: "magnifier",
 },
 {
 step: 2,
 title: "Assessment & Specification",
 description:
 "Tie type and density specified to current engineering guidance, with a written report and quotation specific to your property.",
 icon: "file",
 },
 {
 step: 3,
 title: "Installation",
 description:
 "Mechanical or resin-fixed remedial ties installed by trained operatives. Existing corroded ties isolated where required.",
 icon: "drill",
 },
 {
 step: 4,
 title: "Verification & Making-Good",
 description:
 "Installation verified, mortar beds and drill points made good, and a completion record with insurance-backed guarantee handed over.",
 icon: "certificate",
 },
 ],
 qualificationTitle: "Is Wall Tie Replacement Right For Your Property?",
 qualificationItems: [
 "Cavity wall construction with embedded metal ties",
 "Horizontal cracking at regular intervals or stepped cracking near corners",
 "Bulging, leaning or movement that warrants a structural inspection",
 "Cracking has been confirmed as tie-related rather than subsidence or thermal",
 "Access available for safe working at elevation",
 ],
 includedItems: [
 "Free pre-installation inspection",
 "Borescope cavity check",
 "Structural assessment and written report",
 "Tested / CE-marked remedial tie systems",
 "Existing corroded ties isolated where required",
 "Making-good of drill points and mortar beds",
 "Installation record",
 "Insurance-backed guarantee registration",
 ],
 trustSectionTitle: "Evidence-Led Inspection, Tested Tie Systems",
 trustSectionBody:
 "Every recommendation traces back to something we observed: borescope footage from inside the cavity, crack pattern mapping and tie condition. We only recommend replacement where it's actually needed, and install under an insurance-backed guarantee.",
 benefits: [
 {
 title: "Tie Type Matched To Construction",
 description: "Specified for your wall, not a one-size-fits-all kit.",
 },
 {
 title: "Correct Density & Pattern",
 description: "Specified to current engineering guidance.",
 },
 {
 title: "Corroded Ties Isolated",
 description: "Where required, so they don't continue to expand.",
 },
 {
 title: "Cavity Condition Checked",
 description: "Borescope evidence shared as part of the report.",
 },
 ],
  testimonialsFull: [
    {
      name: "Richard Worsnop",
      date: "July 2026",
      serviceTag: "Home Energy Upgrades",
      source: "trustpilot",
      quote:
        "As a homeowner you often have people in to fix things and I can honestly say this company is exceptional. From the initial approach through to completion everything was carried out in a friendly, respectful and professional way, from the initial survey and admin office through to the fitters who carried out the work. People turned up when they said they would and did the work with skill and efficiency.",
    },
    {
      name: "Cerianne E",
      date: "November 2024",
      serviceTag: "Home Improvement Works",
      source: "google",
      quote:
        "Very professional, everyone I spoke to or came in contact with. Workmen left my home clean and tidy. I would recommend them.",
    },
    {
      name: "Colin Freeman",
      date: "August 2023",
      serviceTag: "Installation Works",
      source: "trustpilot",
      quote:
        "Can only highly recommend this company. Fantastic service from start to finish. All the people we have dealt with have been exceptionally helpful. Could not be happier.",
    },
  ],
 faqs: [
 {
 question: "How do I know my wall ties have failed?",
 answer:
 "Common indicators include regular horizontal cracking at roughly every sixth course (around 450mm), stepped cracking near corners, bulging or leaning walls, and cracking around lintels. A borescope inspection and crack mapping confirms whether ties are the actual cause.",
 },
 {
 question: "Is my home dangerous?",
 answer:
 "In most cases, no. Wall tie corrosion is usually a gradual mechanism that gives plenty of warning through visible cracking before it becomes a structural concern. A structural inspection tells you where you actually stand and what (if anything) needs doing.",
 },
 {
 question: "How disruptive is the work?",
 answer:
 "Remedial tie installation is largely external. We drill small holes from outside, install the new ties through the cavity, then make good. There's typically no internal disruption beyond what's needed for elevation access.",
 },
 {
 question: "What tie systems do you use?",
 answer:
 "Tested, CE-marked mechanical or resin-fixed remedial tie systems, selected to suit your wall construction and exposure. The choice is made at the assessment stage based on what we observe in the cavity.",
 },
 {
 question: "Will you repair the cracks?",
 answer:
 "Making-good of drill points and any opened mortar beds is part of the install. Cosmetic crack repair beyond that can be scoped at quotation if required.",
 },
 {
 question: "Could it be something other than wall ties?",
 answer:
 "Yes. Cracking can also indicate subsidence, thermal movement or sulphate attack. We diagnose the actual cause first and won't recommend tie replacement if it isn't the answer.",
 },
 ],
 ctaTitle: "Book Your Free Inspection",
 sectionOrder: ["trust", "testimonials", "pricing", "faq"],
};
